import { path } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Label } from '@/components/form'
import {
  SharedSelectorItem,
  SharedSelectorList,
  CustomSharedSelectorList,
  CustomSharedSelectorItem
} from '@/components/selector-list'
import { select } from '@/store'
import { FormStack, FormGrid, SectionTitle } from '../../elements'

const Amenities = ({
  amenitiesSelectionData,
  availableAmenitiesList,
  label,
  listName,
  listId,
  amenityTranslationContext,
  showOptions,
  options = ['private', 'shared']
}) => {
  const { t } = useTranslation()

  return (
    <div>
      <Label strong bottom={20}>
        {label}
      </Label>
      <SharedSelectorList
        name={listName}
        id={listId}
        value={amenitiesSelectionData}
        defaultType={options[0]}
      >
        {availableAmenitiesList.map(feature => (
          <SharedSelectorItem
            key={feature.txt_id}
            id={feature.txt_id}
            featureName={feature.name}
            value={feature.txt_id}
            showOptions={showOptions}
            options={options}
          >
            {t(`${amenityTranslationContext}.${feature.txt_id}.text`)}
          </SharedSelectorItem>
        ))}
      </SharedSelectorList>
    </div>
  )
}

const CustomAmenities = ({
  amenitiesSelectionData,
  label,
  listName,
  listId,
  addText,
  placeholder,
  showOptions,
  options = ['private', 'shared']
}) => (
  <div>
    <Label strong bottom={20}>
      {label}
    </Label>
    <CustomSharedSelectorList
      name={listName}
      id={listId}
      value={amenitiesSelectionData}
      addText={addText}
      placeholder={placeholder}
      defaultType={options[0]}
    >
      {amenitiesSelectionData.map(amenity => (
        <CustomSharedSelectorItem
          key={amenity.name}
          value={amenity}
          showOptions={showOptions}
          options={options}
        >
          {amenity.name}
        </CustomSharedSelectorItem>
      ))}
    </CustomSharedSelectorList>
  </div>
)

const FeaturesSection = ({ data, listingId }) => {
  const { t } = useTranslation()
  const features = useSelector(select.basic.features)
  const unitScope = useSelector(path(['listing', listingId, 'unit', 'unit_type_scope_txt_id']))

  return (
    <FormStack>
      <SectionTitle>
        {t('l.contract.edit.features_included.subtitle', 'Select All Features Included')}
      </SectionTitle>
      <FormGrid>
        <Amenities
          amenitiesSelectionData={data.contract_features}
          availableAmenitiesList={features}
          label={t('l.listing.edit.standard_features.label', 'Standard features')}
          listName="contract_features"
          listId="contract_features"
          amenityTranslationContext="basic:b.listing.unit_features"
          showOptions={unitScope !== 'entire'}
        />
        <CustomAmenities
          amenitiesSelectionData={data.contract_custom_features}
          label={t('l.listing.edit.custom_features.label', 'Custom features')}
          listName="contract_custom_features"
          listId="contract_custom_features"
          addText={t('l.contract.edit.add_custom_feature.label', 'Add custom feature')}
          placeholder={t(
            'l.contract.edit.enter_custom_feature.placeholder',
            'Enter custom feature'
          )}
          showOptions={unitScope !== 'entire'}
        />
      </FormGrid>
    </FormStack>
  )
}

const UtilitiesSection = ({ data, listingId }) => {
  const { t } = useTranslation()
  const utilities = useSelector(select.basic.utilities)
  const unitScope = useSelector(path(['listing', listingId, 'unit', 'unit_type_scope_txt_id']))

  return (
    <FormStack>
      <SectionTitle>
        {t('l.contract.edit.utilities_included.subtitle', 'Select All Utilities Included')}
      </SectionTitle>
      <FormGrid>
        <Amenities
          amenitiesSelectionData={data.contract_utilities}
          availableAmenitiesList={utilities}
          label={t('l.contract.edit.standard_utilities.label', 'Standard Utilities')}
          listName="contract_utilities"
          listId="contract_utilities"
          amenityTranslationContext="basic:b.listing.utilities"
          showOptions={unitScope !== 'entire'}
          options={['included', 'shared', 'metered']}
        />
        <CustomAmenities
          amenitiesSelectionData={data.contract_custom_utilities}
          label={t('l.contract.edit.custom_utilities.label', 'Custom Utilities')}
          listName="contract_custom_utilities"
          listId="contract_custom_utilities"
          addText={t('l.contract.edit.add_custom_utility.label', 'Add custom utility')}
          placeholder={t(
            'l.contract.edit.enter_custom_utility.placeholder',
            'Enter custom utility'
          )}
          showOptions={unitScope !== 'entire'}
          options={['included', 'shared', 'metered']}
        />
      </FormGrid>
    </FormStack>
  )
}

export { FeaturesSection, UtilitiesSection }
